import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { TagInput } from "@/components/admin/TagInput";
import { Field, PageTitle, btnDanger, btnPrimary, btnSecondary, btnSmall, card, inputCls, linkCls, textareaCls } from "@/components/admin/ui";
import { PassesEditor, PeopleEditor, VenueEditor } from "@/pages/admin/FestivalEditors";
import { TrailerEditor } from "@/pages/admin/TrailerEditor";
import { MaterialIcon } from "@/components/MaterialIcon";
import { resolveUrl } from "@/lib/api";

const PARAGRAPH_MAX = 1200;
const oneParagraph = (text) => text.replace(/\s*\n+\s*/g, " ").slice(0, PARAGRAPH_MAX);

const Section = ({ children }) => <div className={`${card} p-5 space-y-5`}>{children}</div>;

const OrderList = ({ ids, videos, onChange }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const byId = Object.fromEntries((videos || []).map((v) => [v.id, v]));
  const move = (from, to) => {
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };
  if (!ids.length) return <p className="text-[12px] text-[#6B7280] mb-4">No videos attached yet — pick them below.</p>;
  return (
    <ol className="rounded-md border border-[#E5E7EB] divide-y divide-[#E5E7EB] mb-4 overflow-hidden" data-testid="festival-video-order-list">
      {ids.map((vid, i) => (
        <li
          key={vid}
          draggable
          onDragStart={() => setDragIndex(i)}
          onDragOver={(e) => {
            e.preventDefault();
            if (dragIndex !== null && dragIndex !== i) {
              move(dragIndex, i);
              setDragIndex(i);
            }
          }}
          onDragEnd={() => setDragIndex(null)}
          className={`flex items-center gap-3 px-3 py-2 bg-white cursor-grab active:cursor-grabbing ${dragIndex === i ? "opacity-50" : ""}`}
          data-testid={`festival-video-order-${vid}`}
        >
          <MaterialIcon name="drag_indicator" size={20} className="text-[#9CA3AF]" />
          <span className="w-5 text-[12px] text-[#6B7280]">{i + 1}</span>
          <div className="w-[56px] h-[32px] rounded bg-[#E5E7EB] overflow-hidden shrink-0">
            {byId[vid]?.thumbnail_url && <img src={resolveUrl(byId[vid].thumbnail_url)} alt="" className="w-full h-full object-cover" />}
          </div>
          <span className="flex-1 truncate text-[13px]">{byId[vid]?.title || "Untitled"}</span>
          <button type="button" className={btnSmall} onClick={() => onChange(ids.filter((x) => x !== vid))} data-testid={`festival-video-remove-${vid}`}>
            Remove
          </button>
        </li>
      ))}
    </ol>
  );
};

const VideosPicker = ({ videos, selected, onChange }) => {
  const [q, setQ] = useState("");
  const list = (videos || []).filter((v) => v.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="festival-videos-picker">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-[15px] font-semibold">Videos in this festival</p>
          <p className="text-[12px] text-[#6B7280]">{selected.length} selected · drag to reorder how they appear in “Here’s What You’ll Experience”.</p>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter videos…" className={`${inputCls} w-full sm:w-[220px] h-9`} data-testid="festival-videos-filter" />
      </div>
      <OrderList ids={selected} videos={videos} onChange={onChange} />
      <div className="max-h-[260px] overflow-auto rounded-md border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
        {list.map((v) => (
          <label key={v.id} className="flex items-center gap-3 px-3 py-2 text-[13px] cursor-pointer hover:bg-[#F9FAFB]">
            <input type="checkbox" checked={selected.includes(v.id)} onChange={() => onChange(selected.includes(v.id) ? selected.filter((x) => x !== v.id) : [...selected, v.id])} className="accent-[#B644DC]" data-testid={`festival-video-checkbox-${v.id}`} />
            <span className="truncate flex-1">{v.title}</span>
            {v.festival_name && !selected.includes(v.id) && <span className="text-[11px] text-[#9CA3AF] shrink-0">in {v.festival_name}</span>}
          </label>
        ))}
        {list.length === 0 && <p className="px-3 py-4 text-[12px] text-[#6B7280]">No videos match.</p>}
      </div>
    </div>
  );
};

export default function AdminFestivalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: festival, setData, error } = useApi(`/admin/festivals/${id}`);
  const { data: styles, reload: reloadStyles } = useApi("/admin/dance-styles");
  const { data: videos } = useApi("/admin/videos");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (festival && !form) {
      const { id: _id, dance_styles, video_count, created_at, trailer, trailer_type, trailer_youtube_id, trailer_bunny_video_id, trailer_status, trailer_thumbnail_url, video_order, ...rest } = festival;
      setForm({ ...rest, venue: rest.venue || {}, bands: rest.bands || [], instructors: rest.instructors || [], passes: rest.passes || [], dance_style_ids: rest.dance_style_ids || [], video_ids: rest.video_ids || [] });
    }
  }, [festival, form]);

  if (error)
    return (
      <div data-testid="festival-not-found">
        <p className="text-[15px] font-semibold">Festival not found</p>
        <Link to="/admin/festivals" className={`text-[13px] ${linkCls}`}>← Back to festivals</Link>
      </div>
    );
  if (!festival || !form) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, passes: form.passes.map((p) => ({ ...p, features: p.features.filter((f) => f.trim()).slice(0, 5) })) };
      const { data } = await api.put(`/admin/festivals/${id}`, payload);
      setData(data);
      toast.success("Festival saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${festival.name}”? Videos stay but lose this festival template.`)) return;
    try {
      await api.delete(`/admin/festivals/${id}`);
      toast.success("Festival deleted");
      navigate("/admin/festivals");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createStyle = async (name) => {
    const { data } = await api.post("/admin/dance-styles", { name });
    await reloadStyles();
    return data;
  };

  const firstVideo = form.video_ids[0];

  return (
    <div data-testid="admin-festival-detail-page">
      <p className="text-[13px] mb-3"><Link to="/admin/festivals" className={linkCls} data-testid="back-to-festivals">← Festivals</Link></p>
      <PageTitle title="Festival details">
        <Link to={`/festivals/${id}`} target="_blank" className={btnSecondary} data-testid="preview-festival-page-button">Preview festival page</Link>
        {firstVideo && <Link to={`/watch/${firstVideo}`} target="_blank" className={btnSecondary} data-testid="preview-festival-button">Preview on watch page</Link>}
        <button type="button" className={btnPrimary} onClick={save} disabled={saving} data-testid="save-festival-button">{saving ? "Saving…" : "Save changes"}</button>
      </PageTitle>

      <div className="space-y-5">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-6">
            <div className="space-y-5">
              <Field label="Festival Name"><input value={form.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} data-testid="festival-name-input" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Start date"><input type="date" value={form.start_date || ""} onChange={(e) => set({ start_date: e.target.value || null })} className={inputCls} data-testid="festival-start-date" /></Field>
                <Field label="End date"><input type="date" value={form.end_date || ""} onChange={(e) => set({ end_date: e.target.value || null })} className={inputCls} data-testid="festival-end-date" /></Field>
              </div>
              <Field label="Location" hint="City shown under the festival name, e.g. Sherbrooke, Québec, Canada"><input value={form.location || ""} onChange={(e) => set({ location: e.target.value })} className={inputCls} data-testid="festival-location-input" /></Field>
              <Field label="Features summary" hint="One line, e.g. 2 Live Bands, 30 Instructors, 38 Competitions, 4 floors"><input value={form.features_summary || ""} onChange={(e) => set({ features_summary: e.target.value })} className={inputCls} data-testid="festival-features-input" /></Field>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-[13px] font-semibold text-[#111] mb-1.5">Festival logo</p>
                <ImageUpload value={form.logo_url} onChange={(url) => set({ logo_url: url })} aspect="3 / 1" hint="Logo (wide, transparent PNG works best)" testId="festival-logo-upload" />
                <div className="mt-2 text-[12px] text-[#6B7280] leading-[1.7]" data-testid="festival-logo-size-hint">
                  <p className="font-semibold text-[#111]">Recommended size</p>
                  <p>900 × 300 px (3:1 wide)</p>
                  <p>Transparent PNG · under 2 MB</p>
                </div>
              </div>
              <div>
                <p className="text-[13px] font-semibold text-[#111] mb-1.5">Cover image</p>
                <ImageUpload value={form.cover_image_url} onChange={(url) => set({ cover_image_url: url })} aspect="285 / 160" hint="Cover image" testId="festival-cover-upload" />
                <div className="mt-2 text-[12px] text-[#6B7280] leading-[1.7]" data-testid="festival-cover-size-hint">
                  <p className="font-semibold text-[#111]">Recommended size</p>
                  <p>1280 × 720 px (16:9 landscape)</p>
                  <p>JPG, PNG or WebP · under 2 MB</p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        <Section><TrailerEditor festivalId={id} trailer={festival.trailer} onChange={setData} /></Section>

        <Section>
          <Field label="About This Festival" hint={`One paragraph · ${(form.about || "").length}/${PARAGRAPH_MAX}`}>
            <textarea value={form.about || ""} onChange={(e) => set({ about: oneParagraph(e.target.value) })} className={textareaCls} data-testid="festival-about-input" />
          </Field>
        </Section>

        <Section><PeopleEditor title="Live Bands Performing This Year" nameLabel="Band" items={form.bands} onChange={(bands) => set({ bands })} testId="bands-editor" /></Section>
        <Section><PeopleEditor title="Instructors" nameLabel="Instructor" items={form.instructors} onChange={(instructors) => set({ instructors })} testId="instructors-editor" styleOptions={styles || []} /></Section>
        <Section><VenueEditor venue={form.venue} onChange={(venue) => set({ venue })} /></Section>

        <Section>
          <Field label="Who Is This Festival For?" hint={`One paragraph · ${(form.who_for || "").length}/${PARAGRAPH_MAX}`}>
            <textarea value={form.who_for || ""} onChange={(e) => set({ who_for: oneParagraph(e.target.value) })} className={textareaCls} data-testid="festival-who-for-input" />
          </Field>
          <Field label="Dance Styles" hint="Type to autofill existing dance styles; press Enter to add. New names are created as new styles.">
            <TagInput value={form.dance_style_ids} options={styles || []} onChange={(dance_style_ids) => set({ dance_style_ids })} onCreate={createStyle} placeholder="Search dance styles…" testId="festival-style-tags" />
          </Field>
        </Section>

        <Section><PassesEditor passes={form.passes} onChange={(passes) => set({ passes })} /></Section>
        <Section><VideosPicker videos={videos} selected={form.video_ids} onChange={(video_ids) => set({ video_ids })} /></Section>

        <div className={`${card} p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
          <div>
            <p className="text-[13px] font-semibold">Danger zone</p>
            <p className="text-[12px] text-[#6B7280]">Deletes this festival template. Videos are kept.</p>
          </div>
          <button type="button" className={btnDanger} onClick={remove} data-testid="delete-festival-button">Delete festival</button>
        </div>
        <div className="flex justify-end">
          <button type="button" className={btnPrimary} onClick={save} disabled={saving} data-testid="save-festival-button-bottom">{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}
